import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { type ProjectMemberView } from '@apm/contracts';
import { Avatar } from '../../../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

/**
 * Tối đa bấy nhiêu người hiện trong danh sách sau khi lọc.
 *
 * Một dự án lớn có hàng trăm thành viên; đổ hết vào một danh sách thì không ai
 * cuộn, mà cũng không ai cần — người dùng gõ tên để thu hẹp.
 */
const MAX_OPTIONS = 30;

/**
 * Thành viên khớp từ khoá — theo tên, email **và** phòng ban, tối đa
 * `MAX_OPTIONS` người.
 */
export function matchMembers(
  members: readonly ProjectMemberView[],
  search: string,
): readonly ProjectMemberView[] {
  const needle = search.trim().toLowerCase();
  return (
    needle === ''
      ? members
      : members.filter((member) =>
          `${member.displayName} ${member.email} ${member.departmentName ?? ''}`
            .toLowerCase()
            .includes(needle),
        )
  ).slice(0, MAX_OPTIONS);
}

/**
 * Ô chọn người làm của form tạo/sửa việc — APM-TASK-assign.
 *
 * ## Một nút mở danh sách, cùng giao diện với cột Phụ trách
 *
 * Đóng lại thì ô là **avatar + tên** người đang giữ việc — đúng thứ bảng Danh
 * sách vẽ ở cột Phụ trách — và bấm vào thì bung ra chính `AssigneeMenu` mà ô
 * sửa nhanh ở đó dùng (người dùng chốt 2026-09-25). Hai giao diện cho cùng một
 * câu hỏi "giao cho ai" là hai thứ phải học, và chúng sẽ trôi khỏi nhau.
 *
 * ## Vì sao không phải `<select>`
 *
 * Ô chọn cũ đổ toàn bộ thành viên dự án thành một danh sách phẳng. Với đội năm
 * người thì được; với dự án ba mươi người thì người dùng phải cuộn tìm đúng một
 * cái tên mà họ *đã biết từ đầu*. Gõ vài chữ nhanh hơn hẳn.
 *
 * `Popover` của Radix chứ không tự dựng: ô này nằm **trong** một dialog, và
 * Escape phải đóng danh sách chứ không đóng luôn cả form. Form nghe Escape ở
 * `window`, nên Radix đóng popover thôi chưa đủ — phải chặn phím lan ra ngoài
 * (`onEscapeKeyDown`), không thì một lần Escape mất sạch những gì vừa gõ.
 */
export function AssigneePicker({
  members,
  value,
  onChange,
  disabled = false,
  label: fieldLabel,
  hideLabel = false,
}: {
  readonly members: readonly ProjectMemberView[];
  /** `''` = chưa giao cho ai. */
  readonly value: string;
  readonly onChange: (userId: string) => void;
  readonly disabled?: boolean;
  /** Nhãn ô — mặc định "Người làm"; mốc dùng "Người chịu trách nhiệm". */
  readonly label?: string;
  /** Ẩn nhãn hiện trên ô (vẫn giữ `aria-label`) — khi chỗ đặt đã có nhãn riêng. */
  readonly hideLabel?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const heading = fieldLabel ?? t('taskForm.assignee');
  const [open, setOpen] = useState(false);

  const chosen = members.find((member) => member.userId === value) ?? null;
  const name = chosen === null ? null : chosen.displayName || chosen.email;

  return (
    <div className="flex flex-col gap-1 text-xs">
      {hideLabel ? null : <span>{heading}</span>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={heading}
            disabled={disabled}
            className="flex h-9 w-full items-center gap-2 rounded-control border border-input bg-background px-2 text-left text-sm hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Avatar name={name} size="sm" />
            <span
              className={cn('min-w-0 flex-1 truncate', name === null && 'text-muted-foreground')}
            >
              {name ?? t('taskForm.unassigned')}
            </span>
            <ChevronDown aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" onEscapeKeyDown={(event) => event.stopPropagation()}>
          <AssigneeMenu
            members={members}
            current={value === '' ? null : value}
            onPick={(userId) => {
              if ((userId ?? '') !== value) onChange(userId ?? '');
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Danh sách chọn người: ô tìm + "chưa giao" + từng thành viên có avatar.
 *
 * Dùng ở **hai** chỗ: bung ra dưới avatar ở cột Phụ trách của bảng Danh sách,
 * và trong `AssigneePicker` của form. Component này chỉ là nội dung — đóng mở
 * là việc của chỗ bọc nó (`QuickEdit` ở bảng, `Popover` ở form).
 *
 * ## Hiện cả phòng ban
 *
 * Hai người trùng tên là chuyện thường trong một công ty (APM-1315). Thiếu
 * phòng ban thì người giao việc phải đoán, và giao nhầm thì không ai biết cho
 * tới lúc việc trễ. Ai chưa gán phòng thì **không** hiện dấu phân cách treo
 * lửng — xem `ProjectMemberView.departmentName`.
 */
export function AssigneeMenu({
  members,
  current,
  onPick,
}: {
  readonly members: readonly ProjectMemberView[];
  /** `null` = chưa giao cho ai. */
  readonly current: string | null;
  /** `null` = bỏ giao. */
  readonly onPick: (assigneeId: string | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const options = matchMembers(members, search);

  return (
    <span className="flex flex-col gap-1.5 text-left">
      <input
        type="text"
        // Mở danh sách là để đổi người — gõ tên được ngay, không phải bấm thêm lần nữa.
        autoFocus
        aria-label={t('taskForm.assigneeSearch')}
        placeholder={t('taskForm.assigneeSearch')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          // Enter chọn người đầu tiên khớp — gõ vài chữ rồi Enter là đủ. Chặn
          // mặc định để Enter không gửi luôn form đang bọc ô này.
          if (event.key === 'Enter') {
            event.preventDefault();
            if (options[0]) onPick(options[0].userId);
          }
        }}
        className="h-8 w-full rounded-control border border-input bg-background px-2 text-sm"
      />
      <ul className="flex max-h-56 flex-col overflow-y-auto">
        <li>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted',
              current === null && 'bg-primary/10',
            )}
            onClick={() => onPick(null)}
          >
            <Avatar name={null} size="sm" />
            {t('taskForm.unassigned')}
          </button>
        </li>
        {options.map((member) => {
          const name = member.displayName || member.email;
          return (
            <li key={member.userId}>
              <button
                type="button"
                /*
                  `aria-label` tường minh vì tên suy từ nội dung bị **dính
                  chữ**: khoảng trắng nằm ở đầu `<span>` phòng ban và bị bỏ khi
                  ghép, nên trình đọc màn hình đọc ra "Nguyễn Thu Hà· Kỹ thuật".
                */
                aria-label={
                  member.departmentName === null ? name : `${name} · ${member.departmentName}`
                }
                aria-current={member.userId === current ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-sm hover:bg-muted',
                  member.userId === current && 'bg-primary/10',
                )}
                onClick={() => onPick(member.userId)}
              >
                <Avatar name={name} size="sm" />
                <span className="min-w-0 flex-1 truncate">
                  {name}
                  {member.departmentName ? (
                    <span className="text-muted-foreground"> · {member.departmentName}</span>
                  ) : null}
                </span>
                {member.userId === current ? (
                  <Check aria-hidden className="size-3.5 shrink-0 text-primary" />
                ) : null}
              </button>
            </li>
          );
        })}
        {options.length === 0 ? (
          <li className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('taskForm.assigneeNone')}
          </li>
        ) : null}
      </ul>
    </span>
  );
}
